import React, { useState, useEffect, useMemo } from 'react';
import { vendorsAPI, vendorTypesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import RequireCap, { useCap } from '../components/auth/RequireCap';
import VendorTypesDialog from '../components/vendor/VendorTypesDialog';
import VendorViewDialog from '../components/vendor/VendorViewDialog';
import VendorAttachments from '../components/vendor/VendorAttachments';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Search, Truck, Mail, Pencil, Trash2, Settings2, Eye } from 'lucide-react';

const PAYMENT_METHODS = ['Check', 'EFT', 'NACHA', 'Credit Card', 'Wire', 'ACH'];
const TYPE_1099 = ['1', '2', '3', '4', '5', '6', '7', 'NEC', 'MISC'];

const emptyVendor = {
  name: '', code: '',
  contact_name: '', contact_email: '', contact_phone: '',
  attention: '',
  // Legacy field is kept read-only in UI (backend still stores it if present)
  address: '',
  address1: '', address2: '', city: '', state: '', zip_code: '',
  remit_same_as_vendor: true,
  remit_address1: '', remit_address2: '', remit_city: '', remit_state: '', remit_zip: '',
  payment_terms: 'Net 30',
  lead_time_days: 7,
  is_active: true,
  collects_sales_tax: true,
  tax_exemption_cert: '',
  exemption_expiration_date: '',
  vendor_type_id: '',
  is_1099: false,
  tax_id: '',
  type_of_1099: '',
  default_payment_method: '',
  taxable: false,
  notes: '',
  contract_on_file: false,
  has_approver: false,
  winteam_vendor_number: '',
};

export default function Vendors() {
  const { hasRole } = useAuth();
  const canEdit = useCap('purchase_orders', 'edit');
  const [vendors, setVendors] = useState([]);
  const [vendorTypes, setVendorTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showTypesDialog, setShowTypesDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingVendor, setViewingVendor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyVendor });

  const vtLabel = useMemo(() => {
    const m = new Map(vendorTypes.map(t => [t.id, t.label]));
    return (id) => (id ? m.get(id) || '—' : '—');
  }, [vendorTypes]);

  useEffect(() => {
    loadVendors();
    loadVendorTypes();
  }, []);

  const loadVendors = async () => {
    try {
      const response = await vendorsAPI.list();
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to load vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVendorTypes = async () => {
    try {
      const { data } = await vendorTypesAPI.list({ include_inactive: false });
      setVendorTypes(data);
    } catch (error) {
      console.error('Failed to load vendor types:', error);
    }
  };

  const filteredVendors = vendors.filter(v => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (
      v.name.toLowerCase().includes(q) ||
      v.code.toLowerCase().includes(q) ||
      (v.winteam_vendor_number || '').toLowerCase().includes(q)
    );
  });

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyVendor });
    setShowDialog(true);
  };

  const openEdit = (vendor) => {
    setEditingId(vendor.id);
    setForm({
      ...emptyVendor,
      ...Object.fromEntries(Object.entries(vendor).map(([k, v]) => [k, v ?? emptyVendor[k] ?? ''])),
      // Booleans need explicit coercion (undefined → default)
      is_active: vendor.is_active !== false,
      collects_sales_tax: vendor.collects_sales_tax !== false,
      remit_same_as_vendor: vendor.remit_same_as_vendor !== false,
      is_1099: !!vendor.is_1099,
      taxable: !!vendor.taxable,
      contract_on_file: !!vendor.contract_on_file,
      has_approver: !!vendor.has_approver,
    });
    setShowDialog(true);
  };

  const openView = (vendor) => {
    setViewingVendor(vendor);
    setShowViewDialog(true);
  };

  const editFromView = () => {
    if (viewingVendor) {
      const v = viewingVendor;
      setShowViewDialog(false);
      // Give the view dialog a beat to close before opening the edit dialog
      // so focus management doesn't conflict.
      setTimeout(() => openEdit(v), 100);
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.code) {
      toast.error('Name and code are required');
      return;
    }
    setSaving(true);
    try {
      // Empty strings → null for optional fields so backend doesn't store noise.
      const clean = (v) => (v === '' ? null : v);
      const payload = {
        ...form,
        contact_email: clean(form.contact_email),
        contact_phone: clean(form.contact_phone),
        contact_name: clean(form.contact_name),
        attention: clean(form.attention),
        address: clean(form.address),
        address1: clean(form.address1),
        address2: clean(form.address2),
        city: clean(form.city),
        state: clean(form.state),
        zip_code: clean(form.zip_code),
        remit_address1: form.remit_same_as_vendor ? null : clean(form.remit_address1),
        remit_address2: form.remit_same_as_vendor ? null : clean(form.remit_address2),
        remit_city: form.remit_same_as_vendor ? null : clean(form.remit_city),
        remit_state: form.remit_same_as_vendor ? null : clean(form.remit_state),
        remit_zip: form.remit_same_as_vendor ? null : clean(form.remit_zip),
        tax_exemption_cert: clean(form.tax_exemption_cert),
        exemption_expiration_date: clean(form.exemption_expiration_date),
        vendor_type_id: clean(form.vendor_type_id) || null,
        tax_id: clean(form.tax_id),
        type_of_1099: clean(form.type_of_1099),
        default_payment_method: clean(form.default_payment_method),
        notes: clean(form.notes),
        winteam_vendor_number: clean(form.winteam_vendor_number),
      };
      if (editingId) {
        await vendorsAPI.update(editingId, payload);
        toast.success('Vendor updated');
      } else {
        await vendorsAPI.create(payload);
        toast.success('Vendor created');
      }
      setShowDialog(false);
      setForm({ ...emptyVendor });
      setEditingId(null);
      loadVendors();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join('; ') : 'Failed to save vendor';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Delete vendor "${vendor.name}"? This cannot be undone.`)) return;
    try {
      await vendorsAPI.delete(vendor.id);
      toast.success('Vendor deleted');
      loadVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Vendors</h1>
          <p className="text-slate-500 mt-1">Manage supplier relationships</p>
        </div>
        <div className="flex gap-2">
          <RequireCap module="purchase_orders" action="edit">
            <Button
              variant="outline"
              onClick={() => setShowTypesDialog(true)}
              data-testid="manage-vendor-types-btn"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Manage Types
            </Button>
          </RequireCap>
          <RequireCap module="purchase_orders" action="add">
            <Button onClick={openCreate} data-testid="add-vendor-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Vendor
            </Button>
          </RequireCap>
        </div>
      </div>

      <VendorTypesDialog
        open={showTypesDialog}
        onOpenChange={setShowTypesDialog}
        onSaved={loadVendorTypes}
      />

      <VendorViewDialog
        open={showViewDialog}
        onOpenChange={setShowViewDialog}
        vendor={viewingVendor}
        vendorTypeLabel={viewingVendor ? vtLabel(viewingVendor.vendor_type_id) : ''}
        onEdit={editFromView}
        canEdit={canEdit}
      />

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle data-testid="vendor-dialog-title">{editingId ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
            <DialogDescription>{editingId ? 'Update vendor details' : 'Add a new supplier to the system'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 mt-2 max-h-[70vh] overflow-y-auto pr-2">
            {/* Basics */}
            <section className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vendor Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme Uniforms" data-testid="vendor-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Code *</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="ACME" data-testid="vendor-code-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vendor Type</Label>
                  <Select
                    value={form.vendor_type_id || 'none'}
                    onValueChange={(v) => setForm({ ...form, vendor_type_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="vendor-type-select"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {vendorTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>WinTeam Vendor # <span className="text-xs text-slate-400">(reference)</span></Label>
                  <Input
                    value={form.winteam_vendor_number}
                    onChange={(e) => setForm({ ...form, winteam_vendor_number: e.target.value })}
                    placeholder="Auto-populated on import"
                    data-testid="vendor-winteam-input"
                  />
                </div>
              </div>
            </section>

            {/* Contact */}
            <section className="space-y-4 border-t pt-4">
              <p className="text-sm font-semibold text-slate-700">Contact</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name</Label>
                  <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="John Smith" data-testid="vendor-contact-input" />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="john@acme.com" data-testid="vendor-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="(555) 123-4567" data-testid="vendor-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label>Attention</Label>
                  <Input value={form.attention} onChange={(e) => setForm({ ...form, attention: e.target.value })} placeholder="ATTN: Dept" data-testid="vendor-attention-input" />
                </div>
              </div>
            </section>

            {/* Primary address */}
            <section className="space-y-4 border-t pt-4">
              <p className="text-sm font-semibold text-slate-700">Primary Address</p>
              <div className="space-y-2">
                <Label>Address Line 1</Label>
                <Input value={form.address1} onChange={(e) => setForm({ ...form, address1: e.target.value })} placeholder="123 Supplier St" data-testid="vendor-address1-input" />
              </div>
              <div className="space-y-2">
                <Label>Address Line 2</Label>
                <Input value={form.address2} onChange={(e) => setForm({ ...form, address2: e.target.value })} placeholder="Suite 100" data-testid="vendor-address2-input" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 col-span-2 sm:col-span-1">
                  <Label>City</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="vendor-city-input" />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} maxLength={2} placeholder="TX" data-testid="vendor-state-input" />
                </div>
                <div className="space-y-2">
                  <Label>Zip</Label>
                  <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder="75001" data-testid="vendor-zip-input" />
                </div>
              </div>
            </section>

            {/* Remittance */}
            <section className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">Remittance / Pay-to Address</p>
                <div className="flex items-center gap-3">
                  <Label className="text-sm">Same as primary</Label>
                  <Switch
                    checked={form.remit_same_as_vendor}
                    onCheckedChange={(v) => setForm({ ...form, remit_same_as_vendor: v })}
                    data-testid="vendor-remit-same-switch"
                  />
                </div>
              </div>
              {!form.remit_same_as_vendor && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Remit Address 1</Label>
                    <Input value={form.remit_address1} onChange={(e) => setForm({ ...form, remit_address1: e.target.value })} data-testid="vendor-remit-address1-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Remit Address 2</Label>
                    <Input value={form.remit_address2} onChange={(e) => setForm({ ...form, remit_address2: e.target.value })} data-testid="vendor-remit-address2-input" />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2 col-span-2 sm:col-span-1">
                      <Label>Remit City</Label>
                      <Input value={form.remit_city} onChange={(e) => setForm({ ...form, remit_city: e.target.value })} data-testid="vendor-remit-city-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Remit State</Label>
                      <Input value={form.remit_state} onChange={(e) => setForm({ ...form, remit_state: e.target.value })} maxLength={2} data-testid="vendor-remit-state-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Remit Zip</Label>
                      <Input value={form.remit_zip} onChange={(e) => setForm({ ...form, remit_zip: e.target.value })} data-testid="vendor-remit-zip-input" />
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* AP fields */}
            <section className="space-y-4 border-t pt-4">
              <p className="text-sm font-semibold text-slate-700">AP / Financial</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Payment Terms</Label>
                  <Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} data-testid="vendor-terms-input" />
                </div>
                <div className="space-y-2">
                  <Label>Default Payment Method</Label>
                  <Select
                    value={form.default_payment_method || 'none'}
                    onValueChange={(v) => setForm({ ...form, default_payment_method: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="vendor-payment-method-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Lead Time (days)</Label>
                  <Input type="number" value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: parseInt(e.target.value) || 7 })} data-testid="vendor-leadtime-input" />
                </div>
                <div className="flex items-center justify-between pt-6">
                  <Label>Taxable</Label>
                  <Switch checked={form.taxable} onCheckedChange={(v) => setForm({ ...form, taxable: v })} data-testid="vendor-taxable-switch" />
                </div>
                <div className="flex items-center justify-between pt-6">
                  <Label>Active</Label>
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="vendor-active-switch" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center justify-between pt-2">
                  <Label>Issue 1099</Label>
                  <Switch checked={form.is_1099} onCheckedChange={(v) => setForm({ ...form, is_1099: v })} data-testid="vendor-1099-switch" />
                </div>
                <div className="space-y-2">
                  <Label>Tax ID</Label>
                  <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} placeholder="XX-XXXXXXX" data-testid="vendor-taxid-input" />
                </div>
                <div className="space-y-2">
                  <Label>1099 Type</Label>
                  <Select
                    value={form.type_of_1099 || 'none'}
                    onValueChange={(v) => setForm({ ...form, type_of_1099: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="vendor-1099-type-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {TYPE_1099.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* Sales Tax */}
            <section className="space-y-4 border-t pt-4">
              <p className="text-sm font-semibold text-slate-700">Sales Tax</p>
              <div className="flex items-center justify-between">
                <Label>Collects Sales Tax</Label>
                <Switch
                  checked={form.collects_sales_tax}
                  onCheckedChange={(v) => setForm({ ...form, collects_sales_tax: v })}
                  data-testid="vendor-collects-tax-switch"
                />
              </div>
              {!form.collects_sales_tax && (
                <p className="text-xs text-amber-600" data-testid="vendor-no-tax-note">
                  POs for this vendor will suppress the tax line — use tax may apply.
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tax Exemption Certificate #</Label>
                  <Input value={form.tax_exemption_cert} onChange={(e) => setForm({ ...form, tax_exemption_cert: e.target.value })} placeholder="Optional" data-testid="vendor-exemption-cert-input" />
                </div>
                <div className="space-y-2">
                  <Label>Exemption Expiration Date</Label>
                  <Input type="date" value={form.exemption_expiration_date} onChange={(e) => setForm({ ...form, exemption_expiration_date: e.target.value })} data-testid="vendor-exemption-expiration-input" />
                  <p className="text-xs text-slate-500">Notifies 30 days before expiration</p>
                </div>
              </div>
            </section>

            {/* Operational */}
            <section className="space-y-4 border-t pt-4">
              <p className="text-sm font-semibold text-slate-700">Operational</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between">
                  <Label>Contract on File?</Label>
                  <Switch checked={form.contract_on_file} onCheckedChange={(v) => setForm({ ...form, contract_on_file: v })} data-testid="vendor-contract-switch" />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Has Approver?</Label>
                  <Switch checked={form.has_approver} onCheckedChange={(v) => setForm({ ...form, has_approver: v })} data-testid="vendor-approver-switch" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes about this vendor" data-testid="vendor-notes-textarea" />
              </div>
            </section>

            {/* Attachments — only exposed after the vendor exists (needs an id) */}
            {editingId && (
              <section className="border-t pt-4">
                <VendorAttachments vendorId={editingId} />
              </section>
            )}

            <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-vendor-btn">
              {saving ? 'Saving...' : editingId ? 'Update Vendor' : 'Create Vendor'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by name, code, or WT#..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="vendors-search" />
          </div>
        </CardContent>
      </Card>

      <Card>
        {loading ? (
          <div className="p-8 space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-16 skeleton-pulse rounded"></div>)}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-32 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVendors.map(vendor => {
                const cityStateZip = [vendor.city, [vendor.state, vendor.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                return (
                <TableRow key={vendor.id} data-testid={`vendor-row-${vendor.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                        <Truck className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="min-w-0">
                        <span className="font-medium truncate block">{vendor.name}</span>
                        {vendor.winteam_vendor_number && (
                          <p className="text-xs text-slate-400">WT #{vendor.winteam_vendor_number}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{vtLabel(vendor.vendor_type_id)}</TableCell>
                  <TableCell>
                    <div className="text-sm leading-tight">
                      {vendor.address1 || vendor.address ? (
                        <>
                          <p className="text-slate-900 truncate max-w-[220px]" data-testid={`vendor-address1-${vendor.id}`}>
                            {vendor.address1 || vendor.address}
                          </p>
                          {cityStateZip && <p className="text-slate-500 text-xs">{cityStateZip}</p>}
                        </>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm font-mono text-slate-600" data-testid={`vendor-phone-${vendor.id}`}>
                    {vendor.contact_phone || <span className="text-slate-400 font-sans">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <p>{vendor.contact_name || <span className="text-slate-400">—</span>}</p>
                      {vendor.contact_email && (
                        <p className="text-slate-500 flex items-center gap-1 text-xs">
                          <Mail className="w-3 h-3" />
                          <span className="truncate max-w-[180px]">{vendor.contact_email}</span>
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant={vendor.is_active ? 'default' : 'secondary'}>{vendor.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openView(vendor)}
                        title="View"
                        data-testid={`view-vendor-${vendor.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <RequireCap module="purchase_orders" action="edit">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(vendor)} title="Edit" data-testid={`edit-vendor-${vendor.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </RequireCap>
                      <RequireCap module="purchase_orders" action="delete">
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(vendor)} title="Delete" data-testid={`delete-vendor-${vendor.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </RequireCap>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
              {filteredVendors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <Truck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No vendors found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
